import type {FileMetadata, FileStorageAdapter} from "@/types/FileStorageInterfaces.ts";
import {IndexedDBAdapter} from "@/adapters/IndexedDBFileStorageAdapter.ts";
import {ServerAdapter} from "@/adapters/ServerFileStorageAdapter.ts";
import {v4 as uuidv4} from 'uuid';
import {useAuthClient} from '@/stores/auth-store.ts'

import { watch } from 'vue';

const {rememberedUserKey, sessionStatus} = useAuthClient();

export class HybridAdapter implements FileStorageAdapter {
  private indexedDBAdapter: IndexedDBAdapter;
  private serverAdapter: ServerAdapter;
  private isOnline: boolean = navigator.onLine;
  private syncInProgress = false;

  constructor(serverBaseUrl: string) {
    this.indexedDBAdapter = new IndexedDBAdapter();
    this.serverAdapter = new ServerAdapter(serverBaseUrl);

    window.addEventListener('online', () => {
      this.isOnline = true;
      void this.runSyncCycle();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    watch(sessionStatus, (status) => {
      if (status === 'connected') void this.runSyncCycle();
    });
    if (this.isOnline && sessionStatus.value === 'connected') void this.runSyncCycle();
  }

  private getUserScope(): string {
    return rememberedUserKey.value || 'anonymous';
  }

  async store(file: File | Uint8Array, metadata: Omit<FileMetadata, 'id'>, id?: string): Promise<string> {
    const fileId = id || uuidv4();
    const userScope = this.getUserScope();

    await this.indexedDBAdapter.store(file, metadata, fileId);

    if (this.isOnline && sessionStatus.value === 'connected') {
      try {
        await this.serverAdapter.store(file, metadata, fileId);
        // Avoid immediate reconcile HEAD calls; mark locally as synced on successful upload response.
        await this.indexedDBAdapter.markFileSynced(fileId);
        await this.indexedDBAdapter.removeSyncOperation(userScope, fileId);
        return fileId;
      } catch (error) {
        console.warn('Server upload failed, using local storage:', error);
        await this.indexedDBAdapter.enqueueSyncOperation(userScope, fileId, 'upload');
        return fileId;
      }
    }

    await this.indexedDBAdapter.enqueueSyncOperation(userScope, fileId, 'upload');
    return fileId;
  }

  async retrieve(id: string): Promise<Blob> {
    try {
      return await this.indexedDBAdapter.retrieve(id);
    } catch (error) {
      if (this.isOnline && sessionStatus.value === 'connected') {
        return await this.serverAdapter.retrieve(id);
      }
      throw error;
    }
  }

  async delete(id: string): Promise<boolean> {
    const userScope = this.getUserScope();
    let localDeleted = false;
    let serverDeleted = false;

    try {
      localDeleted = await this.indexedDBAdapter.delete(id);
    } catch (error) {
      console.warn('Failed to delete from local storage:', error);
    }

    if (this.isOnline && sessionStatus.value === 'connected') {
      try {
        serverDeleted = await this.serverAdapter.delete(id);
      } catch (error) {
        console.warn('Failed to delete from server:', error);
      }
    }

    if (serverDeleted) {
      await this.indexedDBAdapter.removeSyncOperation(userScope, id, 'delete');
    } else {
      await this.indexedDBAdapter.enqueueSyncOperation(userScope, id, 'delete');
    }

    await this.indexedDBAdapter.removeSyncOperation(userScope, id, 'upload');
    return localDeleted || serverDeleted;
  }

  async getUrl(id: string): Promise<string> {
    try {
      return await this.indexedDBAdapter.getUrl(id);
    } catch (error) {
      if (this.isOnline && sessionStatus.value === 'connected') {
        return await this.serverAdapter.getUrl(id);
      }
      throw error;
    }
  }

  async exists(id: string): Promise<boolean> {
    const localExists = await this.indexedDBAdapter.exists(id);
    if (localExists) return true;

    if (this.isOnline && sessionStatus.value === 'connected') {
      return await this.serverAdapter.exists(id);
    }

    return false;
  }

  private async runSyncCycle(): Promise<void> {
    if (!this.isOnline || this.syncInProgress || sessionStatus.value !== 'connected') return;
    this.syncInProgress = true;

    try {
      await this.syncPendingOperations();
    } finally {
      this.syncInProgress = false;
    }
  }

  private async syncPendingOperations(): Promise<void> {
    const userScope = this.getUserScope();
    const pendingOps = await this.indexedDBAdapter.listDueSyncOperations(userScope);

    for (const op of pendingOps) {
      if (sessionStatus.value !== 'connected' || this.getUserScope() !== userScope) break;
      try {
        if (op.operation === 'upload') {
          await this.syncUpload(op.fileId, userScope);
        } else {
          await this.syncDelete(op.fileId, userScope);
        }
      } catch (error) {
        await this.indexedDBAdapter.markSyncOperationFailed(op.id, error, op.attempts + 1);
      }
    }
  }

  private async syncUpload(id: string, userScope: string): Promise<void> {
    const blob = await this.indexedDBAdapter.retrieve(id);
    const metadata = await this.indexedDBAdapter.getFileMetadata(id);
    if (!metadata) {
      await this.indexedDBAdapter.removeSyncOperation(userScope, id, 'upload');
      return;
    }

    const arrayBuffer = await blob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const {id: _ignoredId, ...metadataWithoutId} = metadata;
    await this.serverAdapter.store(uint8Array, metadataWithoutId, id);
    await this.indexedDBAdapter.markFileSynced(id);

    await this.indexedDBAdapter.removeSyncOperation(userScope, id, 'upload');
  }

  private async syncDelete(id: string, userScope: string): Promise<void> {
    const result = await this.serverAdapter.deleteWithStatus(id);
    if (result.ok || result.status === 404) {
      await this.indexedDBAdapter.removeSyncOperation(userScope, id, 'delete');
      await this.indexedDBAdapter.removeSyncOperation(userScope, id, 'upload');
      return;
    }

    throw new Error(`Delete failed with status ${result.status}`);
  }
}
